<script setup>
import { userAuthState } from '@/@core/auth'
import { cookieRef } from '@/@layouts/stores/config'
import ConfirmDialog from '@/components/dialogs/ConfirmDialog.vue'
import { VDataTableServer } from 'vuetify/labs/VDataTable'

definePage({
  meta: {
    name: 'escalation',
    subject: 'global.menu',
    action: 'escalation',
  },
})

const snackbar = useToast()
const unitId = cookieRef('selectedUnit')
const auth = userAuthState()

const escalationTypes = await getEnumValue('EscalationTypes', true)

const userModuleNames = auth?.user?.modules?.map(m => m.name) ?? []

const escalationTypeToModule = {
  SERVICE_DESK_TICKET: 'Service Desk',
  SURVEY: 'Survey',
}

const escalationTypesFiltered = computed(() => {
  return (escalationTypes.value ?? [])
    .filter(item => {
      const requiredModule = escalationTypeToModule[item.value]

      return !requiredModule || userModuleNames.includes(requiredModule)
    })
    .map(item => ({
      ...item,
      title: item.value
        .split('_')
        .map(word => word.charAt(0) + word.slice(1).toLowerCase())
        .join(' '),
    }))
})

const selectedEscalationType = ref(escalationTypes?.value[0]?.value)

const searchQuery = ref('')
const debouncedSearch = refDebounced(searchQuery, 500)

// Data table options
const itemsPerPage = ref(10)
const page = ref(1)
const sortBy = ref()
const orderBy = ref()

const updateOptions = options => {
  page.value = options.page
  sortBy.value = options.sortBy[0]?.key
  orderBy.value = options.sortBy[0]?.order
}

// Headers
const headers = [
  { title: '', key: 'data-table-expand' },
  { title: 'Department', key: 'name', sortable: false },
  { title: 'Levels', key: 'levels', sortable: false },
]

const {
  data: escalationData,
  isFetching: isFetchingEscalations,
  execute: fetchEscalations,
} = useApi(createUrl('/configurations/escalations', {
  query: {
    paged: 1,
    q: debouncedSearch,
    unit_id: unitId,
    escalation_type: selectedEscalationType,
    itemsPerPage,
    page,
    sort: sortBy,
    order_by: orderBy,
  },
}))

const escalations = computed(() => escalationData.value?.data)
const totalEscalations = computed(() => escalationData.value?.meta?.total)

const isConfirmDialogVisible = ref(false)
const confirmDialogData = ref()

const showConfirmDialog = data => {
  confirmDialogData.value = data
  isConfirmDialogVisible.value = true
}

watch(isConfirmDialogVisible, () => {
  if (!isConfirmDialogVisible.value) confirmDialogData.value = null
})

const deleteEscalation = async (status, { departmentId, categoryId }) => {
  if (status) {
    const api = categoryId
      ? `/configurations/escalations/${unitId.value}/${departmentId}/${categoryId}`
      : `/configurations/escalations/${unitId.value}/${departmentId}`

    await $api(api, { method: 'DELETE' }).then(() => {
      snackbar.success('Escalation deleted successfully', 'success')
      fetchEscalations()
    })
  }
}

// Function to group escalations by category
const groupByCategory = data => {
  const group = data.reduce((acc, value) => {
    const key = value.child_category?.id ?? value.category?.id

    if (!acc[key]) {
      let title = ''

      if (value.child_category) {
        title = `${value.child_category.name} - ${value.category.name}`
      } else if (value.category) {
        title = value.category.name
      }

      acc[key] = {
        category_id: value.category?.id ?? null,
        child_category_id: value.child_category?.id ?? null,
        title: title,
        escalations: [],
      }
    }

    acc[key].escalations.push(value)

    return acc
  }, {})

  return Object.values(group).sort((a, b) => {
    if (a.category_id === null) return -1
    if (b.category_id === null) return 1

    return 0
  })
}

// ===== Import Escalation Functionality =====
const refImportEscalationForm = ref()
const importEscalationFile = ref()
const importEscalationErrors = ref()
const isImportEscalationLoading = ref(false)
const isImportEscalationModal = ref(false)

const submitImportEscalation = async () => {
  if (!importEscalationFile.value) {
    importEscalationErrors.value = {
      file: ["Please select a file to import."],
    }

    return
  }
  isImportEscalationLoading.value = true
  importEscalationErrors.value = null

  const formData = new FormData()

  formData.append("file", importEscalationFile.value)
  if (unitId.value) {
    formData.append("unit_id", unitId.value)
  }

  try {
    await $api("import/escalations", {
      method: "POST",
      body: formData,
    })
    snackbar.success("Escalations imported successfully.")
    isImportEscalationModal.value = false
    fetchEscalations()
    importEscalationFile.value = null
  } catch (e) {
    const errorData = e.data

    importEscalationErrors.value = errorData?.errors || errorData?.message || {
      message: errorData?.message || "File upload failed. Please try again.",
    }
    if (!importEscalationErrors.value && !errorData?.message)
      importEscalationErrors.value = { message: "Import submission failed." }
  } finally {
    isImportEscalationLoading.value = false
  }
}

const downloadSampleEscalationFile = async () => {
  await downloadFile(
    "configurations/escalations/sample-import-file/export",
    "escalations-sample-excel.xlsx",
    () => {},
    () => {},
  )
}

watch(
  () => isImportEscalationModal.value,
  value => {
    if (value === false) {
      importEscalationFile.value = null
      importEscalationErrors.value = null
    }
  },
)
// ===== End Import Escalation Functionality =====
</script>

<template>
  <section>
    <VCard
      variant="flat"
      class="border"
    >
      <VCardText class="d-flex flex-wrap py-4 gap-4">
        <div style="inline-size: 14rem;">
          <AppSelect
            v-model="selectedEscalationType"
            :items="escalationTypesFiltered"
            placeholder="Select Type"
            density="compact"
          />
        </div>

        <VSpacer />

        <div class="app-user-search-filter d-flex align-center flex-wrap gap-4">
          <div style="inline-size: 10rem;">
            <AppTextField
              v-model="searchQuery"
              placeholder="Search"
              density="compact"
            />
          </div>

          <VBtn
            v-if="$can('import', 'configuration.escalation')"
            variant="flat"
            prepend-icon="tabler-download"
            @click="isImportEscalationModal = !isImportEscalationModal"
          >
            Import Escalations
          </VBtn>

          <RouterLink
            v-if="$can('create', 'configuration.escalation')"
            :to="{ name: 'app-global-escalation-create-update', params: { escalationType: selectedEscalationType, departmentId: 'create' } }"
          >
            <VBtn
              variant="flat"
              prepend-icon="tabler-plus"
            >
              Add Escalation
            </VBtn>
          </RouterLink>
        </div>
      </VCardText>

      <VDivider />

      <VDataTableServer
        v-model:items-per-page="itemsPerPage"
        v-model:page="page"
        :items="escalations"
        :items-length="totalEscalations"
        :headers="headers"
        :loading="isFetchingEscalations"
        class="text-no-wrap"
        expand-on-click
        @update:options="updateOptions"
      >
        <template #item.name="{ item }">
          <span class="text-base font-weight-medium">
            {{ item.name ?? '—' }}
          </span>
        </template>

        <template #item.levels="{ item }">
          <div class="d-flex flex-row gap-4">
            <VChip
              v-for="level in item.escalations.slice(0, 5)"
              :key="level"
              pill
              color="primary"
              text-color="white"
              class="me-4 cursor-pointer non-selectable"
            >
              {{ level.escalation_level.level }}
            </VChip>
            <VChip
              v-if="item.escalations.length > 5"
              pill
              color="primary"
              text-color="white"
              class="cursor-pointer non-selectable"
            >
              ...
            </VChip>
          </div>
        </template>

        <!-- Expanded Row Data -->
        <template #expanded-row="slotProps">
          <tr class="v-data-table__tr">
            <td
              colspan="12"
              class="level-bg-color pb-4"
            >
              <VContainer
                v-for="(category, key) in groupByCategory(slotProps.item.escalations)"
                :key="key"
              >
                <div class="d-flex align-center">
                  <p class="me-auto text-h6 mt-4">
                    {{ category.title }}
                  </p>
                  <IconBtn
                    v-if="$can('delete', 'configuration.escalation')"
                    @click="() => {
                      showConfirmDialog({
                        departmentId: slotProps.item.id,
                        categoryId: category.category_id,
                      })
                    }"
                  >
                    <VIcon icon="tabler-trash" />
                  </IconBtn>
                  <IconBtn v-if="$can('update', 'configuration.escalation')">
                    <RouterLink
                      :to="{
                        name: 'app-global-escalation-create-update',
                        params: {
                          escalationType: selectedEscalationType,
                          departmentId: slotProps.item.id,
                          categoryId: category.category_id,
                          childCategoryId: category.child_category_id,
                        },
                      }"
                      class="font-weight-medium text-link"
                    >
                      <VIcon icon="tabler-edit" />
                    </RouterLink>
                  </IconBtn>
                </div>

                <VCard
                  variant="outlined"
                  class="w-100"
                >
                  <VTable class="text-no-wrap">
                    <thead>
                      <tr>
                        <th>Level</th>
                        <th>Type</th>
                        <th>Users</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="item in category.escalations.sort((a, b) => a.escalation_level_id - b.escalation_level_id)"
                        :key="item.escalation_level_id"
                      >
                        <td>{{ item.escalation_level.level }}</td>
                        <td>{{ item.escalation_type ?? '—' }}</td>
                        <td>
                          <AppAvatarGroup
                            :items="item.escalation_users"
                            :max="4"
                            :size="36"
                            name-key="name"
                            avatar-key="avatar"
                          />
                        </td>
                      </tr>
                    </tbody>
                  </VTable>
                </VCard>
              </VContainer>
            </td>
          </tr>
        </template>
      </VDataTableServer>
    </VCard>

    <ConfirmDialog
      v-model:isDialogVisible="isConfirmDialogVisible"
      confirmation-question="Are you sure?"
      :is-confirm-visible="false"
      :data="confirmDialogData"
      @confirm="deleteEscalation"
    />

    <!-- Import Escalation Modal -->
    <VDialog
      v-model="isImportEscalationModal"
      max-width="600"
      :persistent="isImportEscalationLoading"
    >
      <DialogCloseBtn
        size="small"
        :disabled="isImportEscalationLoading"
        @click="isImportEscalationModal = false"
      />
      <VCard>
        <VCardText class="pt-2">
          <h6 class="text-lg font-weight-medium mt-5">
            Import Escalations
          </h6>
        </VCardText>

        <VCardText>
          <VForm ref="refImportEscalationForm">
            <VFileInput
              label="Upload File"
              prepend-icon="tabler-file-import"
              accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
              :disabled="isImportEscalationLoading"
              :error-messages="importEscalationErrors?.file"
              @change="
                (e) => {
                  importEscalationFile = e.target.files[0];
                }
              "
            />
          </VForm>
        </VCardText>

        <!-- General Errors -->
        <VCardText
          v-if="
            importEscalationErrors?.message &&
              !Array.isArray(importEscalationErrors)
          "
        >
          <VAlert
            color="error"
            variant="tonal"
            closable
            @click:close="importEscalationErrors.message = null"
          >
            {{ importEscalationErrors.message }}
          </VAlert>
        </VCardText>

        <!-- Validation Errors -->
        <VCardText v-if="Array.isArray(importEscalationErrors) && importEscalationErrors.length > 0">
          <div class="d-flex align-center justify-space-between mb-1">
            <div class="text-error text-caption font-weight-medium">
              {{ importEscalationErrors.length }}
              {{ importEscalationErrors.length === 1 ? 'Error' : 'Errors' }} found
            </div>
            <VBtn
              icon
              color="error"
              variant="text"
              size="small"
              class="ma-0 pa-0"
              density="compact"
              @click="importEscalationErrors = []"
            >
              <VIcon
                color="error"
                size="18"
                icon="tabler-x"
              />
            </VBtn>
          </div>
          <div
            class="d-flex flex-column gap-2 scrollable-errors"
            style="max-height: 90px; overflow-y: auto;"
          >
            <VAlert
              v-for="(error, index) in importEscalationErrors"
              :key="index"
              color="error"
              variant="tonal"
              density="compact"
              style="flex-shrink: 0;"
            >
              {{ error.row ? `Row ${error.row}: ` : "" }}{{ error.message || error }}
            </VAlert>
          </div>
        </VCardText>

        <!-- Loading Bar -->
        <VCardText
          v-if="isImportEscalationLoading"
          class="pt-0 pb-2"
        >
          <VProgressLinear
            indeterminate
            color="primary"
            height="6"
            rounded
            class="mb-1"
          />
        </VCardText>

        <!-- Action Buttons -->
        <VCardActions class="px-5 pb-5">
          <VBtn
            variant="flat"
            color="primary"
            :disabled="isImportEscalationLoading"
            @click="downloadSampleEscalationFile"
          >
            Download Sample
          </VBtn>
          <VSpacer />
          <VBtn
            variant="flat"
            color="error"
            :disabled="isImportEscalationLoading"
            @click="isImportEscalationModal = false"
          >
            Close
          </VBtn>
          <VBtn
            variant="flat"
            :loading="isImportEscalationLoading"
            :disabled="isImportEscalationLoading || !importEscalationFile"
            @click="submitImportEscalation"
          >
            Import
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>
  </section>
</template>

<style scoped>
.level-bg-color {
  background-color: #f7f7f7;
}

.non-selectable {
  user-select: none !important;
}
</style>

<style lang="css">
.v-data-table-footer {
  border-block-start: thin solid rgba(var(--v-border-color), var(--v-border-opacity));
  margin-block-start: 0 !important;
  padding-block: 0.5rem;
}

/* For Chrome, Edge, Safari */
.scrollable-errors::-webkit-scrollbar {
  width: 2px;
}

.scrollable-errors::-webkit-scrollbar-thumb {
  background-color: rgba(0,0,0,0.2);
  border-radius: 2px;
}

.scrollable-errors::-webkit-scrollbar-track {
  background-color: transparent;
}

/* For Firefox */
.scrollable-errors {
  scrollbar-width: thin;
  scrollbar-color: rgba(0,0,0,0.2) transparent;
}
</style>
